import { configureStore } from "@reduxjs/toolkit";
import { persistStore, persistReducer } from "redux-persist";
import storage from "redux-persist/lib/storage";
import { persistedHealthCheckReducer } from "./healthCheckSlice";
import authReducer, { logoutDueToError } from "./authSlice";
import yachtReducer from "./yachtSlice";
import userManagementFiltersReducer from "./userManagementFiltersSlice";
import publishReducer from "./publishSlice";
import harborCapabilitiesReducer from "./harborCapabilitiesSlice";
import serviceProviderCapabilitiesReducer from "./serviceProviderCapabilitiesSlice";
import clubCapabilitiesReducer from "./clubCapabilitiesSlice";
import baseApi from "@/services/RootBaseApi";
import { yachtManagementApi } from "@/services/YatchManagementService";
import { clubRegistrationApi } from "@/services/ClubRegistrationService";
import { harborAssociationApi } from "@/services/HarborAssociationService";
import "@/services/MediationService";
import "@/services/LegalComplianceService";
import "@/services/HarborOperatorDashboardService";
import { setOn401Callback } from "@/utils/ApiClient";
import { toast } from "react-toastify";

// Persist configuration for auth
const authPersistConfig = {
  key: "auth",
  version: 1,
  storage,
  whitelist: [
    "token",
    "profile_picture",
    "userType",
    "userData",
    "selectedHarborId",
    "selectedCommunityId",
    "selectedServiceProviderId",
    "latestVersion",
    "userPermissions",
  ],
};

// Persist configuration for harbor capabilities
const harborCapabilitiesPersistConfig = {
  key: "harborCapabilities",
  version: 1,
  storage,
  whitelist: ["uniqueKeys"],
};

// Persist configuration for service provider capabilities
const serviceProviderCapabilitiesPersistConfig = {
  key: "serviceProviderCapabilities",
  version: 1,
  storage,
  whitelist: ["uniqueKeys"],
};

// Persist configuration for club capabilities
const clubCapabilitiesPersistConfig = {
  key: "clubCapabilities",
  version: 1,
  storage,
  whitelist: ["uniqueKeys", "subscriptionPlanId"],
};

// Persist configuration for user management filters
const userManagementFiltersPersistConfig = {
  key: "userManagementFilters",
  version: 1,
  storage,
  whitelist: ["startDate", "endDate"],
};

// Create a persisted reducer
const persistedAuthReducer = persistReducer(authPersistConfig, authReducer);
const persistedPublishReducer = persistReducer(
  authPersistConfig,
  publishReducer,
);
const persistedHarborCapabilitiesReducer = persistReducer(
  harborCapabilitiesPersistConfig,
  harborCapabilitiesReducer,
);
const persistedServiceProviderCapabilitiesReducer = persistReducer(
  serviceProviderCapabilitiesPersistConfig,
  serviceProviderCapabilitiesReducer,
);
const persistedClubCapabilitiesReducer = persistReducer(
  clubCapabilitiesPersistConfig,
  clubCapabilitiesReducer,
);
const persistedUserManagementFiltersReducer = persistReducer(
  userManagementFiltersPersistConfig,
  userManagementFiltersReducer,
);

// Configure the store with persisted reducers
export const store = configureStore({
  reducer: {
    auth: persistedAuthReducer,
    yacht: yachtReducer,
    userManagementFilters: persistedUserManagementFiltersReducer,
    healthCheck: persistedHealthCheckReducer,
    publish: persistedPublishReducer,
    harborCapabilities: persistedHarborCapabilitiesReducer,
    serviceProviderCapabilities: persistedServiceProviderCapabilitiesReducer,
    clubCapabilities: persistedClubCapabilitiesReducer,
    [baseApi.reducerPath]: baseApi.reducer,
    [yachtManagementApi.reducerPath]: yachtManagementApi.reducer,
    [clubRegistrationApi.reducerPath]: clubRegistrationApi.reducer,
    [harborAssociationApi.reducerPath]: harborAssociationApi.reducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: ["persist/PERSIST", "persist/REHYDRATE"],
      },
    })
      .concat(baseApi.middleware)
      .concat(yachtManagementApi.middleware)
      .concat(clubRegistrationApi.middleware)
      .concat(harborAssociationApi.middleware),
});

// Configure 401 unauthorized interceptor callback
setOn401Callback(() => {
  store.dispatch(logoutDueToError());
  toast.error("Session expired. Please log in again.", {
    toastId: "session-expired-error",
  });
});

// Persistor for redux-persist
export const persistor = persistStore(store);

// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

export { baseApi } from "@/services/RootBaseApi";
